
// When an event is triggered, the event reports if modifier keys are pressed
// If one or more modifiers are pressed, we add the offset(s) to make it a unique value
// Values are selected because CharCode ranges from 0-65536
const modifierOffsets = {
    shift: 65536,
    ctrl: 131072,
    alt: 262144,
    s: 65536, //shift
    c: 131072, //ctrl
    a: 262144, //alt
};

// Direct map of strings to check before single characters
const specialCharacterMap = {
    "gainFocus": 524288,
    "loseFocus": 524289,
    "enter": 13,
    "esc": 27,
    "tab": 9,
    "space": 32,
    "comma": 188,
    "period": 190,
    "arrowLeft": 37,
    "arrowRight": 39,
    "arrowDown": 40,
    //Add any necessary characters as they are used
};

function convertToKeyCode (key) {

    // Handle passing keycode directly
    if (Number.isInteger(key)) return key;

    // Handle use of modifier key, i.e. CTRL / SHIFT
    if (key.indexOf("+") !== -1) {
        let keySplit = key.split(/\+(.+)/);
        return modifierOffsets[keySplit[0]] + convertToKeyCode(keySplit[1]);
    }

    // Handle special hardcoded characters, mapped for readibility, e.g. "esc"
    if (key in specialCharacterMap) return specialCharacterMap[key];

    // Default case, handle single key, e.g. "a"
    if (key.length === 1) return key.toUpperCase().charCodeAt(0);

    throw new Error("Special character not mapped " + key);
}

function hasInputFieldFocus () {
    let focusedElement = document.activeElement;

    if (!focusedElement) return false;

    let tag = focusedElement.tagName.toLowerCase();
    return (tag === "input" || tag === "textarea");
}

let handlers = [];
document.addEventListener('keydown', (event) => {

    // Make sure not to capture keypresses while user is typing in an input field
    if (hasInputFieldFocus()) return;

    // console.log("keydown:", event.keyCode);// Kept here for debugging

    // Trigger all handlers until event is consumed
    for (let i=0; i<handlers.length; ++i) {
        if (handlers[i].trigger(event)) return;
    }
});


/* -----------------------------------------------------------------------------*/


export default class HotkeyHandler {
    /*
     * Key event listener
     * Components must subscribe to hotkeys on component load
     * and unsubscribe at component unload ( important! ).
     * Handlers can be globally enabled/disabled.
     *
     * Subscribe / Unsubscribe usage:
     *      handler.subscribe("a", keyHandler, this);
     *      handler.subscribe({"a": keyHandler, "b": () => alert("A")}, this);
     *      handler.subscribe({"a": multiKeyHandler, "b": multiKeyHandler}, this);
     *
     * First argument is either a single key to map, or a collection of key:callback pairs
     *
     * Last argument is a unique object to use as a reference, which must be passed when unsubscribing
     * Recommended to use this-keyword when subscribing inside an object.
     *
     * With conflicting hotkeys, the last subscribe will be called,
     * assumption being that this is a child component with higher focus
     * Functions returning false will not consume event, will attempt to call next handler
     *
     * Careful passing props-function directly, they may change after subscribing.
     * Use lambdas or classfunctions instead. E.g.,
     *      DO THIS  :  handler.subscribe("space", () => this.props.clickHandler(), this);
     *      NOT THIS :  handler.subscribe("space", this.props.clickHandler, this);
     */
    constructor (enabled) {
        if (enabled === undefined) enabled = true;

        this.enabled = enabled;
        this.keys = {};

        handlers.unshift(this);
    }

    destroy () {
        let idx = handlers.indexOf(this);
        if (idx >= 0) {
            handlers.splice(idx, 1);
        }
    }

    setEnabled (enabled) {
        let wasEnabled = this.enabled;
        this.enabled = enabled;

        if (wasEnabled && !enabled) {
            this._triggerKeyCode(specialCharacterMap["loseFocus"]);
        } else if (!wasEnabled && enabled) {
            this._triggerKeyCode(specialCharacterMap["gainFocus"]);
        }
    }

    hasFocus () {
        return this.enabled;
    }

    subscribe (key, callback, hashKey) {

        if (key === undefined) return;

        // Subscribe to a single event
        if (typeof key === "string") {
            return this._subscribeSingle(key, callback, hashKey || callback);
        }

        // Subscribe to a collection of events using object notation
        let events = key;
        hashKey = callback;
        for (key in events) {
            this._subscribeSingle(key, events[key], hashKey || events[key]);
        }
    }

    unsubscribe (key, hashKey) {

        if (key === undefined) return;

        // Unsubscribe to a single event
        if (typeof key === "string") {
            return this._unsubscribeSingle(key, hashKey);
        }

        // Unsubscribe to a collection of events using object notation
        let events = key;
        for (key in events) {
            this._unsubscribeSingle(key, hashKey || events[key]);
        }
    }

    trigger (event) {

        if (!this.enabled) return false;

        let keyCode = event.keyCode;
        if (event.shiftKey) keyCode += modifierOffsets.shift;
        if (event.ctrlKey) keyCode += modifierOffsets.ctrl;
        if (event.altKey) keyCode += modifierOffsets.alt;

        if (this._triggerKeyCode(keyCode)) {
            event.preventDefault();
            event.stopPropagation();
            return true;
        }

        return false;
    }

    _triggerKeyCode (keyCode) {
        let handlers = this.keys[keyCode];
        if (handlers === undefined) return false;

        for (let i=0; i<handlers.length; ++i) {
            // Iterate over all the registered handlers until event is consumed
            if (handlers[i].callback() !== false) {
                return true;
            }
        }
        return false;
    }

    _subscribeSingle (key, callback, hashKey) {
        let keyCode = convertToKeyCode(key);
        let handlers = this.keys[keyCode] || [];

        //By adding this callback to the front, we set callback priority to highest
        handlers.unshift({
            hashKey: hashKey,
            callback: callback
        });
        this.keys[keyCode] = handlers;
    }

    _unsubscribeSingle (key, hashKey) {
        let keyCode = convertToKeyCode(key);
        let handlers = this.keys[keyCode];

        if (handlers === undefined) return;

        for (let i=0; i<handlers.length; ++i) {
            if (handlers[i].hashKey === hashKey) {
                handlers.splice(i, 1);
                return;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/js/include/HotkeyHandler.js